import { useContext } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import { TRANSACTION_PARTY_TYPE } from 'components/constants';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { formatMoney } from 'services/utils';
import Button from 'uikit/Button/Button';
import { BENEFICIARY_TYPE } from '../RepeatTransactionModel';
import './RepeatTransactionSummary.scss';

const isFilled = (value) => value !== undefined && value !== null && value !== '';

// Summary of the confirm request - all values are taken from the request body, the form values only give their names
const RepeatTransactionSummary = ({
  dto,
  currency,
  precision,
  commissions,
  documents,
  purposeLabel,
  isLoading,
  onConfirm,
  onClose
}) => {
  const i18n = useContext(i18nContext);
  const requisites = dto.requisites || {};

  const formatAmount = (amount) => [formatMoney(amount, precision), currency].filter(Boolean).join(' ');

  // The request has only tariff ids, the names are taken from the commissions of the form
  const getTariffName = (tariff) => {
    const commission = tariff.tariffId && commissions.find((item) => item.tariffId === tariff.tariffId);

    return commission
      ? commission.tariffName || commission.systemTariffKey || `Tariff ${tariff.tariffId}`
      : i18n.getMessage('createTransaction.payment.commission.original.customCommission');
  };

  const getRequisitesRows = () => {
    if (isFilled(requisites.walletNumber)) {
      return [[i18n.getMessage('createTransaction.payment.toAccount'), requisites.walletNumber]];
    }

    if (isFilled(requisites.cryptoAddress)) {
      return [
        [i18n.getMessage('createTransaction.repeat.cryptoAddress'), requisites.cryptoAddress],
        [i18n.getMessage('createTransaction.repeat.cryptoNetwork'), requisites.cryptoNetwork]
      ];
    }

    return [
      [i18n.getMessage('createTransaction.payment.ibanTab.label'), requisites.iban],
      [i18n.getMessage('createTransaction.payment.accountNumber'), requisites.accountNumber],
      [i18n.getMessage('createTransaction.payment.sortCode'), requisites.sortCode],
      [i18n.getMessage('createTransaction.payment.bankName'), requisites.bankName],
      [i18n.getMessage('createTransaction.payment.bic'), requisites.bic],
      [
        i18n.getMessage('createTransaction.payment.bankCountry'),
        requisites.bankCountry && i18n.getMessage(requisites.bankCountry)
      ]
    ];
  };

  const getBeneficiaryRows = () => {
    const { beneficiary } = dto;

    if (!beneficiary) {
      return [];
    }

    const isCompany = beneficiary.type === BENEFICIARY_TYPE.CORPORATE;

    return [
      [
        i18n.getMessage('createTransaction.repeat.summary.beneficiaryType'),
        i18n.getMessage(
          `transactionPartyType.${isCompany ? TRANSACTION_PARTY_TYPE.COMPANY : TRANSACTION_PARTY_TYPE.INDIVIDUAL}`
        )
      ],
      [i18n.getMessage('createTransaction.payment.companyName'), beneficiary.companyName],
      [i18n.getMessage('createTransaction.payment.firstName'), beneficiary.firstName],
      [i18n.getMessage('createTransaction.payment.lastName'), beneficiary.lastName],
      [i18n.getMessage('createTransaction.payment.registrationNumber'), beneficiary.registrationNumber],
      [
        i18n.getMessage('createTransaction.repeat.address.country'),
        beneficiary.country && i18n.getMessage(beneficiary.country)
      ],
      [i18n.getMessage('createTransaction.repeat.address.address'), beneficiary.address],
      [i18n.getMessage('createTransaction.repeat.address.city'), beneficiary.city],
      [i18n.getMessage('createTransaction.repeat.address.postCode'), beneficiary.postCode],
      [i18n.getMessage('createTransaction.repeat.address.state'), beneficiary.state]
    ];
  };

  // The request has only document ids, the names are taken from the documents of the form
  const getDocumentNames = (ids) =>
    ids.map((id) => documents.find((document) => document.id === id)?.name || id).join(', ');

  const commissionsTotal = dto.tariffs.reduce((sum, tariff) => sum + tariff.amount, 0);

  const sections = [
    {
      key: 'sender',
      title: i18n.getMessage('createTransaction.repeat.summary.section.sender'),
      rows: [[i18n.getMessage('createTransaction.payment.fromAccount'), dto.senderWallet]]
    },
    {
      key: 'requisites',
      title: i18n.getMessage('createTransaction.repeat.section.requisites'),
      rows: getRequisitesRows()
    },
    {
      key: 'beneficiary',
      title: i18n.getMessage('createTransaction.repeat.section.beneficiary'),
      rows: getBeneficiaryRows()
    },
    {
      key: 'payment',
      title: i18n.getMessage('createTransaction.repeat.summary.section.payment'),
      rows: [
        [
          i18n.getMessage('createTransaction.payment.paymentMethod'),
          dto.paymentMethod && i18n.getMessage(`paymentMethod.${dto.paymentMethod}`)
        ],
        [i18n.getMessage('createTransaction.payment.amount.label'), formatAmount(dto.amount)],
        [i18n.getMessage('createTransaction.payment.paymentPurpose.label'), purposeLabel || dto.purpose]
      ]
    },
    {
      key: 'commission',
      title: i18n.getMessage('createTransaction.payment.commission.label'),
      rows: dto.tariffs.map((tariff) => [getTariffName(tariff), formatAmount(tariff.amount)]),
      total: [i18n.getMessage('createTransaction.repeat.summary.commissionTotal'), formatAmount(commissionsTotal)]
    },
    {
      key: 'additional',
      title: i18n.getMessage('createTransaction.repeat.summary.section.additional'),
      rows: [
        [i18n.getMessage('createTransaction.payment.commentToTransfer'), dto.comment],
        [i18n.getMessage('createTransaction.payment.commentToAdmin'), dto.commentToAdmin],
        [
          i18n.getMessage('createTransaction.repeat.summary.originalDocuments'),
          getDocumentNames(dto.originalDocuments)
        ],
        [i18n.getMessage('createTransaction.repeat.summary.attachedDocuments'), getDocumentNames(dto.attachedDocuments)]
      ]
    }
  ]
    .map((section) => ({ ...section, rows: section.rows.filter(([, value]) => isFilled(value)) }))
    .filter((section) => section.rows.length > 0 || section.total);

  // Rows are keyed by the index - commissions can have the same names
  const renderRow = ([label, value], key, className = 'repeat-transaction-summary-row') => (
    <div key={key} className={className}>
      <span className={'repeat-transaction-summary-label'}>{label}</span>
      <span className={'repeat-transaction-summary-value'}>{value}</span>
    </div>
  );

  return (
    <div className={'repeat-transaction-summary'}>
      <div className={'repeat-transaction-summary-header'}>
        <div className={'repeat-transaction-summary-heading'}>
          <h2 className={'repeat-transaction-summary-title'}>
            {i18n.getMessage('createTransaction.repeat.summary.title')}
          </h2>
          <p className={'repeat-transaction-summary-description'}>
            {i18n.getMessage('createTransaction.repeat.summary.description')}
          </p>
        </div>
        <CloseIcon className={'repeat-transaction-summary-close-icon'} onClick={isLoading ? undefined : onClose} />
      </div>

      <div className={'repeat-transaction-summary-body'}>
        {sections.map((section) => (
          <section key={section.key} className={'repeat-transaction-summary-section'}>
            <span className={'repeat-transaction-summary-section-title'}>{section.title}</span>
            {section.rows.map((row, index) => renderRow(row, index))}
            {section.total && renderRow(section.total, 'total', 'repeat-transaction-summary-row total')}
          </section>
        ))}
      </div>

      <div className={'repeat-transaction-summary-actions'}>
        <Button type={'outline'} size={'large'} onClick={onClose} isDisabled={isLoading}>
          {i18n.getMessage('createTransaction.repeat.summary.button.back')}
        </Button>
        <Button type={'primary'} size={'large'} onClick={onConfirm} isDisabled={isLoading}>
          {isLoading ? <Loader /> : i18n.getMessage('createTransaction.repeat.summary.button.confirm')}
        </Button>
      </div>
    </div>
  );
};

RepeatTransactionSummary.propTypes = {
  // The body of the confirm request
  dto: PropTypes.shape({
    senderWallet: PropTypes.string,
    beneficiary: PropTypes.object,
    requisites: PropTypes.object,
    paymentMethod: PropTypes.string,
    amount: PropTypes.number,
    tariffs: PropTypes.arrayOf(PropTypes.shape({ tariffId: PropTypes.number, amount: PropTypes.number })),
    purpose: PropTypes.string,
    comment: PropTypes.string,
    commentToAdmin: PropTypes.string,
    originalDocuments: PropTypes.arrayOf(PropTypes.string),
    attachedDocuments: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  currency: PropTypes.string,
  precision: PropTypes.number,
  commissions: PropTypes.array,
  // Documents of the original transaction and the uploaded ones, to show their names
  documents: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  purposeLabel: PropTypes.string,
  isLoading: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default RepeatTransactionSummary;
